import { useState, useMemo } from 'react';
import { MessageSquare, Plus, Trash2, Loader2, FolderKanban, Building2, Search, Users, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn, getInitials } from '@/lib/utils';
import { CompanyChatView, ProjectChatView } from '@/components/chats';
import {
  useAllChats,
  useCreateCompanyChat,
  useDeleteCompanyChat,
  useMarkCompanyChatAsRead,
  useMarkProjectChatAsRead,
  useProjectChats,
  useFindOrCreatePrivateChat,
} from '@/hooks/use-chats';
import { useUsers } from '@/hooks/use-users';
import { useAuth } from '@/hooks/use-auth';
import type { UnifiedChat, CompanyChat, ProjectChat } from '@/types';

export function CompanyChatsPage() {
  const { user } = useAuth();
  const [selectedChat, setSelectedChat] = useState<UnifiedChat | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<UnifiedChat | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'my-chats' | 'admin-private'>('my-chats');

  const { data: allChats = [], isLoading } = useAllChats();
  const { data: allUsers = [] } = useUsers();
  const createChat = useCreateCompanyChat();
  const deleteChat = useDeleteCompanyChat();
  const markCompanyChatAsRead = useMarkCompanyChatAsRead();
  const markProjectChatAsRead = useMarkProjectChatAsRead();
  const findOrCreatePrivateChat = useFindOrCreatePrivateChat();

  const isAdmin = user?.role === 'admin';

  // Combined search: filter both users and existing chats
  const { searchableUsers, searchableChats } = useMemo(() => {
    if (!userSearchQuery.trim()) return { searchableUsers: [], searchableChats: [] };
    const query = userSearchQuery.toLowerCase();
    
    // Search users (exclude current user and clients)
    const users = allUsers
      .filter((u) => 
        u.id !== user?.id && 
        u.role !== 'client' &&
        (u.displayName.toLowerCase().includes(query) || u.email.toLowerCase().includes(query))
      )
      .slice(0, 5);
    
    // Search existing chats by name
    const chats = allChats
      .filter((chat) => 
        chat.name.toLowerCase().includes(query) ||
        (chat.description && chat.description.toLowerCase().includes(query)) ||
        (chat.projectName && chat.projectName.toLowerCase().includes(query))
      )
      .slice(0, 5);
    
    return { searchableUsers: users, searchableChats: chats };
  }, [allUsers, allChats, userSearchQuery, user?.id]);

  // Group chats by category and sort by unread count (unread first)
  const { companyChats, myPrivateChats, allPrivateChats, projectChats } = useMemo(() => {
    const company: UnifiedChat[] = [];
    const myPrivate: UnifiedChat[] = [];
    const allPrivate: UnifiedChat[] = [];
    const project: UnifiedChat[] = [];
    
    allChats.forEach((chat) => {
      if (chat.category === 'company') {
        if (chat.isPrivate) {
          allPrivate.push(chat);
          // For "my chats", show private chats where I'm a participant
          // When viewing your own private chat, the name shows only the other person's name
          // When admin views others' private chats, name shows "User A & User B" format
          // So if name doesn't contain " & ", I'm a participant
          // BUT for non-admins, they can only see chats they're part of anyway, so show all
          if (!isAdmin || !chat.name.includes(' & ')) {
            myPrivate.push(chat);
          }
        } else {
          company.push(chat);
        }
      } else {
        project.push(chat);
      }
    });
    
    // Sort function: unread first, then alphabetically by name
    const sortByUnread = (a: UnifiedChat, b: UnifiedChat) => {
      // First sort by unread (chats with unread messages come first)
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
      // Within same unread status, sort by unread count (more unread first)
      if (a.unreadCount !== b.unreadCount) return b.unreadCount - a.unreadCount;
      // Then alphabetically by name
      return a.name.localeCompare(b.name);
    };
    
    company.sort(sortByUnread);
    myPrivate.sort(sortByUnread);
    allPrivate.sort(sortByUnread);
    project.sort(sortByUnread);
    
    return { companyChats: company, myPrivateChats: myPrivate, allPrivateChats: allPrivate, projectChats: project };
  }, [allChats, isAdmin]);

  // Mark chat as read when selected
  const handleSelectChat = (chat: UnifiedChat) => {
    setSelectedChat(chat);
    setTimeout(() => {
      if (chat.category === 'company') {
        markCompanyChatAsRead.mutate(chat.id);
      } else {
        markProjectChatAsRead.mutate(chat.id);
      }
    }, 500);
  };

  const handleCreateChat = async (name: string, description?: string) => {
    const newChat = await createChat.mutateAsync({ name, description });
    // Select the newly created chat
    setSelectedChat({
      id: newChat.id,
      name: newChat.name,
      category: 'company',
      description: newChat.description,
      unreadCount: 0,
      createdAt: newChat.createdAt,
      updatedAt: newChat.updatedAt,
    });
    setShowCreateDialog(false);
  };

  const handleStartPrivateChat = async (targetUserId: string, targetUserName: string) => {
    const chat = await findOrCreatePrivateChat.mutateAsync(targetUserId);
    setUserSearchQuery('');
    // Select the chat
    setSelectedChat({
      id: chat.id,
      name: targetUserName,
      category: 'company',
      description: chat.description,
      isPrivate: true,
      unreadCount: 0,
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
    });
  };

  const handleDeleteChat = async () => {
    if (chatToDelete && chatToDelete.category === 'company') {
      await deleteChat.mutateAsync(chatToDelete.id);
      if (selectedChat?.id === chatToDelete.id) {
        setSelectedChat(null);
      }
      setChatToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div className="w-72 border-r flex flex-col bg-background">
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold">Chats</h1>
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCreateDialog(true)}
                title="Create new company chat"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {/* Combined Search: Users and Chats */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search chats or users..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="pl-8 h-9"
            />
            
            {/* Search Results Dropdown */}
            {(searchableChats.length > 0 || searchableUsers.length > 0) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
                {/* Existing Chats */}
                {searchableChats.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                      Chats
                    </div>
                    {searchableChats.map((chat) => (
                      <button
                        key={chat.id}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left"
                        onClick={() => {
                          handleSelectChat(chat);
                          setUserSearchQuery('');
                        }}
                      >
                        <MessageSquare className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{chat.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {chat.category === 'project' ? chat.projectName : 
                             chat.isPrivate ? 'Private chat' : 'Company chat'}
                          </div>
                        </div>
                        {chat.unreadCount > 0 && (
                          <span className="flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-full bg-primary text-primary-foreground">
                            {chat.unreadCount}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Users to start new chat */}
                {searchableUsers.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                      Start new chat with
                    </div>
                    {searchableUsers.map((searchUser) => (
                      <button
                        key={searchUser.id}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left"
                        onClick={() => handleStartPrivateChat(searchUser.id, searchUser.displayName)}
                        disabled={findOrCreatePrivateChat.isPending}
                      >
                        <Avatar className="h-7 w-7">
                          <AvatarImage src={searchUser.avatarUrl || undefined} />
                          <AvatarFallback className="text-xs">{getInitials(searchUser.displayName)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{searchUser.displayName}</div>
                          <div className="text-xs text-muted-foreground truncate">{searchUser.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {userSearchQuery.trim() && searchableChats.length === 0 && searchableUsers.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 p-3 text-center text-sm text-muted-foreground">
                No chats or users found
              </div>
            )}
          </div>
        </div>

        {/* Admin Tabs */}
        {isAdmin ? (
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'my-chats' | 'admin-private')} className="flex-1 flex flex-col">
            <TabsList className="mx-2 mt-2 grid w-auto grid-cols-2">
              <TabsTrigger value="my-chats" className="text-xs">My Chats</TabsTrigger>
              <TabsTrigger value="admin-private" className="text-xs">
                <Shield className="h-3 w-3 mr-1" />
                All Private
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="my-chats" className="flex-1 mt-0">
              <ScrollArea className="h-full">
                <div className="p-2">
                  <ChatListContent
                    companyChats={companyChats}
                    privateChats={myPrivateChats}
                    projectChats={projectChats}
                    selectedChat={selectedChat}
                    onSelectChat={handleSelectChat}
                    onDeleteChat={(chat) => setChatToDelete(chat)}
                    isAdmin={isAdmin}
                    showEmptyState={allChats.length === 0 && !userSearchQuery}
                  />
                </div>
              </ScrollArea>
            </TabsContent>
            
            <TabsContent value="admin-private" className="flex-1 mt-0">
              <ScrollArea className="h-full">
                <div className="p-2">
                  {allPrivateChats.length === 0 ? (
                    <div className="p-4 text-center">
                      <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">No private chats between users</p>
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      {allPrivateChats.map((chat) => (
                        <ChatListItem
                          key={chat.id}
                          chat={chat}
                          isSelected={selectedChat?.id === chat.id}
                          onSelect={() => handleSelectChat(chat)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        ) : (
          <ScrollArea className="flex-1">
            <div className="p-2">
              <ChatListContent
                companyChats={companyChats}
                privateChats={myPrivateChats}
                projectChats={projectChats}
                selectedChat={selectedChat}
                onSelectChat={handleSelectChat}
                isAdmin={false}
                showEmptyState={allChats.length === 0 && !userSearchQuery}
              />
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <ChatViewWrapper chat={selectedChat} />
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">Select a chat to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Chat Dialog */}
      <CreateChatDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateChat}
        isLoading={createChat.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!chatToDelete} onOpenChange={() => setChatToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chat</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{chatToDelete?.name}"? This action cannot be undone and all messages will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChat}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface ChatListContentProps {
  companyChats: UnifiedChat[];
  privateChats: UnifiedChat[];
  projectChats: UnifiedChat[];
  selectedChat: UnifiedChat | null;
  onSelectChat: (chat: UnifiedChat) => void;
  onDeleteChat?: (chat: UnifiedChat) => void;
  isAdmin: boolean;
  showEmptyState: boolean;
}

function ChatListContent({
  companyChats,
  privateChats,
  projectChats,
  selectedChat,
  onSelectChat,
  onDeleteChat,
  isAdmin,
  showEmptyState,
}: ChatListContentProps) {
  const hasAnyChats = companyChats.length > 0 || privateChats.length > 0 || projectChats.length > 0;

  if (showEmptyState || !hasAnyChats) {
    return (
      <div className="p-4 text-center">
        <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">No chats yet</p>
        <p className="text-xs text-muted-foreground mt-1">Search for a user above to start chatting</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Company Chats Section */}
      {companyChats.length > 0 && (
        <div>
          <div className="flex items-center gap-2 px-2 py-1 mb-1">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase">
              Company Chats
            </span>
          </div>
          <div className="space-y-0.5">
            {companyChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChat?.id === chat.id}
                onSelect={() => onSelectChat(chat)}
                onDelete={isAdmin && onDeleteChat ? () => onDeleteChat(chat) : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Private Chats Section */}
      {privateChats.length > 0 && (
        <div>
          <div className="flex items-center gap-2 px-2 py-1 mb-1">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase">
              Private Chats
            </span>
          </div>
          <div className="space-y-0.5">
            {privateChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChat?.id === chat.id}
                onSelect={() => onSelectChat(chat)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Project Chats Section */}
      {projectChats.length > 0 && (
        <div>
          <div className="flex items-center gap-2 px-2 py-1 mb-1">
            <FolderKanban className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase">
              Project Chats
            </span>
          </div>
          <div className="space-y-0.5">
            {projectChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChat?.id === chat.id}
                onSelect={() => onSelectChat(chat)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface ChatViewWrapperProps {
  chat: UnifiedChat;
}

function ChatViewWrapper({ chat }: ChatViewWrapperProps) {
  // For project chats, we need to fetch the full project chat data
  const { data: projectChats } = useProjectChats(chat.projectId || '');
  
  if (chat.category === 'company') {
    // Convert UnifiedChat to CompanyChat format
    const companyChat: CompanyChat = {
      id: chat.id,
      name: chat.name,
      description: chat.description || null,
      createdBy: '',
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
      unreadCount: chat.unreadCount,
    };
    return <CompanyChatView chat={companyChat} />;
  }

  // For project chats, find the full chat data
  const projectChat = projectChats?.find((c) => c.id === chat.id);
  
  if (!projectChat) {
    // Create a minimal ProjectChat object while loading
    const minimalChat: ProjectChat = {
      id: chat.id,
      projectId: chat.projectId || '',
      type: chat.chatType || 'internal',
      name: chat.name,
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
    };
    return <ProjectChatView chat={minimalChat} projectId={chat.projectId || ''} />;
  }

  return <ProjectChatView chat={projectChat} projectId={chat.projectId || ''} />;
}

interface ChatListItemProps {
  chat: UnifiedChat;
  isSelected: boolean;
  onSelect: () => void;
  onDelete?: () => void;
}

function ChatListItem({ chat, isSelected, onSelect, onDelete }: ChatListItemProps) {
  const hasUnread = chat.unreadCount > 0;
  
  return (
    <div
      className={cn(
        'flex items-center justify-between px-3 py-2 rounded-md cursor-pointer group',
        isSelected
          ? 'bg-primary/10 text-primary'
          : 'hover:bg-muted'
      )}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <MessageSquare className="h-4 w-4 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={cn(
              "truncate text-sm",
              hasUnread ? "font-semibold" : "font-medium"
            )}>{chat.name}</span>
            {chat.category === 'project' && chat.chatType && (
              <Badge 
                variant={chat.chatType === 'client' ? 'default' : 'secondary'} 
                className="text-[10px] px-1.5 py-0 shrink-0"
              >
                {chat.chatType}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        {/* Unread count badge */}
        {hasUnread && (
          <span className="flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-full bg-primary text-primary-foreground">
            {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
          </span>
        )}

        {onDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3 w-3 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );
}

interface CreateChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string, description?: string) => void;
  isLoading: boolean;
}

function CreateChatDialog({ open, onOpenChange, onSubmit, isLoading }: CreateChatDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(name.trim(), description.trim() || undefined);
    setName('');
    setDescription('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Company Chat</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., General, Sales, Support"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this chat for?"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Chat'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
